import type { CronMonitorDonutChartProps } from '../types'

export function CronMonitorDonutChart({
  segments,
  totalLabel,
  centerCaption = 'Total runs',
  compact = false,
}: CronMonitorDonutChartProps) {
  const radius = compact ? 46 : 72
  const strokeWidth = compact ? 16 : 22
  const circumference = 2 * Math.PI * radius
  const resolvedTotalLabel = totalLabel
    ?? String(segments.reduce((sum, segment) => sum + segment.value, 0))

  return (
    <div
      className={
        compact
          ? 'flex w-full items-center justify-center gap-5'
          : 'flex w-full flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-5'
      }
    >
      <div className={compact ? 'relative h-32 w-32 shrink-0' : 'relative h-56 w-56 shrink-0'}>
        <svg viewBox="0 0 180 180" className="h-full w-full -rotate-90">
          <circle
            cx="90"
            cy="90"
            r={radius}
            fill="none"
            stroke="#e2e8f0"
            strokeWidth={strokeWidth}
          />
          {segments.map((segment, index) => {
            const dash = (segment.percent / 100) * circumference
            const offset = segments
              .slice(0, index)
              .reduce(
                (sum, previousSegment) =>
                  sum + (previousSegment.percent / 100) * circumference,
                0,
              )
            return (
              <circle
                key={segment.label}
                cx="90"
                cy="90"
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
              />
            )
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className={compact ? 'text-xl font-bold text-slate-900' : 'text-3xl font-bold text-slate-900'}>
            {resolvedTotalLabel}
          </span>
          <span className={compact ? 'text-xs text-slate-500' : 'text-sm text-slate-500'}>{centerCaption}</span>
        </div>
      </div>

      <div className={compact ? 'min-w-0 flex-1 space-y-2.5' : 'min-w-0 w-full flex-1 space-y-3 sm:max-w-none'}>
        {segments.map((segment) => (
          <div key={segment.label} className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: segment.color }}
              />
              <span className={compact ? 'truncate text-xs text-slate-700' : 'truncate text-sm text-slate-700'}>{segment.label}</span>
            </div>
            <div className={compact ? 'shrink-0 text-right text-xs' : 'shrink-0 text-right text-sm'}>
              <span className="font-semibold text-slate-900">{segment.value}</span>
              <span className="ml-2 text-slate-500">({segment.percent}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
